// Log outcome for a call from a known patient: which screen their stage calls for, and what it says.
// Pure, so the call flow and its tests share one answer (CU-869fn2wce).
//
// An enquiry gets the existing contact popup (Book appointment / Spoke - not booked / No answer).
// Every later stage, and an archived patient, gets a short outcome choice instead: that popup's
// cards book a consultation and move the enquiry along, which is wrong for a patient who is
// already booked, in treatment or archived. The choice only records how the call-back went
// (the Call Logs outcome endpoint); anything else about the patient is done on their page.
import { latestTreatmentOf, leadDetailUrl } from '../leadFollowUp/leadFollowUpModel';
import { STATUS, normalizeStatus } from '../../utils/leadActionPopupResolver';

export const LEAD_STAGE = Object.freeze({
    ENQUIRY: 'enquiry',
    BOOKED: 'booked',
    ATTENDED: 'attended',
    IN_TREATMENT: 'in_treatment',
    COMPLETE: 'complete',
    ARCHIVED: 'archived',
});

export { leadDetailUrl };
// The patient lookup hides archived patients (a 404); this read-only one answers for them.
export const archivedLeadUrl = (practiceId, leadId) => `/api/v1/practices/${practiceId}/archived/${leadId}/`;

const STAGE_BY_STATUS = {
    [STATUS.NEW]: LEAD_STAGE.ENQUIRY,
    // Failed to attend: the call is to rebook, which the enquiry popup does.
    [STATUS.FAILED_TO_ATTEND_CONSULTATION]: LEAD_STAGE.ENQUIRY,
    [STATUS.CONSULTATION_BOOKED]: LEAD_STAGE.BOOKED,
    [STATUS.CONSULTATION_ATTENDED]: LEAD_STAGE.ATTENDED,
    [STATUS.TREATMENT_STARTED]: LEAD_STAGE.IN_TREATMENT,
    [STATUS.CLOSED]: LEAD_STAGE.COMPLETE,
    [STATUS.ARCHIVED]: LEAD_STAGE.ARCHIVED,
};

export const leadStageOf = (lead) => {
    if (lead?.is_archived === true) return LEAD_STAGE.ARCHIVED;
    const status = latestTreatmentOf(lead)?.status || lead?.status;
    // An unknown status keeps today's behaviour: the enquiry popup.
    return STAGE_BY_STATUS[normalizeStatus(status)] || LEAD_STAGE.ENQUIRY;
};

export const usesEnquiryPopup = (stage) => stage === LEAD_STAGE.ENQUIRY;

const nameOf = (lead, fallback) => [lead?.first_name, lead?.last_name].filter(Boolean).join(' ').trim() || fallback || 'this patient';

// { heading, detail } for the short outcome choice.
export const stageCopy = (stage, lead, fallbackName) => {
    const name = nameOf(lead, fallbackName);
    const treatment = latestTreatmentOf(lead)?.treatment || '';
    const forTreatment = treatment ? ` for ${treatment}` : '';
    switch (stage) {
        case LEAD_STAGE.BOOKED:
            return { heading: `Call ${name} about their booked consultation${forTreatment}`, detail: 'Consultation booked. To move or cancel the appointment, open the patient.' };
        case LEAD_STAGE.ATTENDED:
            return { heading: `Call ${name} to follow up their consultation${forTreatment}`, detail: 'Consultation attended. To start treatment, open the patient.' };
        case LEAD_STAGE.IN_TREATMENT:
            return { heading: `Call ${name} about their treatment${forTreatment}`, detail: 'Treatment in progress. To book their next appointment, open the patient.' };
        case LEAD_STAGE.COMPLETE:
            return { heading: `Call ${name}: treatment complete${forTreatment}`, detail: 'Treatment completed.' };
        case LEAD_STAGE.ARCHIVED:
            return { heading: `Call ${name} (archived patient)`, detail: 'Saving how the call went keeps them archived. To restore them, open the patient.' };
        default:
            return { heading: `Call ${name}`, detail: '' };
    }
};

// The outcomes offered on the short choice. "Not a patient" is for numbers we don't know, never
// for a patient; the server's list for the row (log_outcome.options) narrows it further when sent.
// An answered call (`answered`) has no "No answer" or "Left voicemail".
const STAGE_OUTCOMES = [
    { key: 'spoke', label: 'Spoke to them' },
    { key: 'no_answer', label: 'No answer' },
    { key: 'left_voicemail', label: 'Left voicemail' },
];
export const stageOutcomeChoices = ({ serverOptions = null, answered = false } = {}) => STAGE_OUTCOMES.filter((o) => (
    (!answered || o.key === 'spoke') && (!Array.isArray(serverOptions) || serverOptions.includes(o.key))
));
